import {Component, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {ColComponent, ContainerComponent, RowComponent} from "@coreui/angular";
import {CharityService} from "../../../services/charity.service";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";
import {ICharity} from "../../../interfaces/charity/ICharity";
import {SnackbarService} from "../../../services/snackbar.service";
import {MatFormField, MatLabel, MatSuffix} from "@angular/material/form-field";
import {MatAutocomplete, MatAutocompleteTrigger, MatOption} from "@angular/material/autocomplete";
import {MatInput} from "@angular/material/input";
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {forkJoin, map, Observable, of, startWith, Subscription} from "rxjs";
import {AsyncPipe, NgIf} from "@angular/common";
import {HasPermissionDirective} from "../../../directives/permissions/has-permission";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {IMovePlayersToAnotherCharity} from "../../../interfaces/charity/IMovePlayersToAnotherCharity";
import {ActiveUserService} from "../../../services/active-user.service";
import {ErrorHandlingService} from "../../../services/error-handling.service";

@Component({
  selector: 'app-move-players-pledges-between-charities',
  standalone: true,
  imports: [
    ContainerComponent,
    RowComponent,
    ColComponent,
    MatFormField,
    MatAutocomplete,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    MatOption,
    MatAutocompleteTrigger,
    AsyncPipe,
    HasPermissionDirective,
    MatButton,
    MatIcon,
    MatProgressSpinner,
    MatIconButton,
    NgIf,
    MatSuffix,
    MatSlideToggle
  ],
  templateUrl: './move-players-pledges-between-charities.component.html',
  styleUrl: './move-players-pledges-between-charities.component.css'
})
export class MovePlayersPledgesBetweenCharitiesComponent implements OnInit, OnDestroy {

  private charityService: CharityService = inject(CharityService);
  private snackBarService: SnackbarService = inject(SnackbarService);
  private activeUserService: ActiveUserService = inject(ActiveUserService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);

  @Input() set gameSetter(gameData: { gameP: IGameQueryResult, isCrossGame: boolean }) {
    this.gameId = gameData.gameP.Id;
    this.fetchUpToDateCharities();
  }

  protected allCharities: ICharity[] = [];
  protected chosenOriginCharityControl: FormControl<ICharity | null> = new FormControl(null);
  protected chosenDestinationCharityControl: FormControl<ICharity | null> = new FormControl(null);
  protected deactivateOriginCharityControl: FormControl<boolean | null> = new FormControl(true);
  protected filteredOriginCharities: Observable<ICharity[]> = of([]);
  protected filteredDestinationCharities: Observable<ICharity[]> = of([]);
  protected sameCharitySelected: boolean = false;
  private charityCategoriesSub: Subscription | null = null;
  private gameId: string = '';

  displayFn(charityP: ICharity): string {
    return charityP && charityP.Name ? charityP.Name : '';
  }

  private fetchUpToDateCharities() {
    this.charityService.fetchActiveCharitiesForGame(this.gameId).subscribe({
      next: (charitiesP: ICharity[]) => {
        this.allCharities = charitiesP;
        this.chosenOriginCharityControl.updateValueAndValidity();
        this.chosenDestinationCharityControl.updateValueAndValidity();
      },
      error: () => {
        this.snackBarService.openErrorSnackBar('Looks like there was an error retrieving the charities for this game. Please refresh to try again');
      }
    })
  }

  ngOnInit() {
    this.filteredOriginCharities = this.chosenOriginCharityControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        this.validateCharitySelection();
        const name = typeof value === 'string' ? value : value?.Name;
        return name ? this._filter(name as string) : this.allCharities.slice();
      }),
    );

    this.filteredDestinationCharities = this.chosenDestinationCharityControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        this.validateCharitySelection();
        const name = typeof value === 'string' ? value : value?.Name;
        return name ? this._filter(name as string) : this.allCharities.slice();
      }),
    );

    this.charityCategoriesSub = this.charityService.charityCategories$.subscribe({
      next: () => {
        this.fetchUpToDateCharities();
      }
    })
  }

  private _filter(name: string): ICharity[] {
    const filterValue = name.toLowerCase();
    return this.allCharities.filter(option => option.Name.toLowerCase().includes(filterValue));
  }


  private validateCharitySelection() {
    const origin = this.chosenOriginCharityControl.getRawValue();
    const destination = this.chosenDestinationCharityControl.getRawValue();
    this.sameCharitySelected = !!origin && !!destination && origin.Id === destination.Id;
  }

  clearDestinationCharity() {
    this.chosenDestinationCharityControl.setValue(null);
  }

  clearOriginCharity() {
    this.chosenOriginCharityControl.setValue(null);
  }

  protected confirmAndSubmitPledgeUpdates() {
    let move_pledges_command: IMovePlayersToAnotherCharity = {
      destinationCharityId: this.chosenDestinationCharityControl.getRawValue()?.Id!,
      gameId: this.gameId,
      adminId: this.activeUserService.activeUser().Id,
      originCharityId: this.chosenOriginCharityControl.getRawValue()?.Id!,
      deactivateOriginCharity: this.deactivateOriginCharityControl.getRawValue()!
    }

    this.charityService.movePlayersToAnotherCharity(move_pledges_command).subscribe({
      next: () => {
        this.snackBarService.openSuccessfulSnackBar('Player Pledges Have successfully been moved!');
        this.resetAllFields();
      },
      error:(error) => {
        this.errorHandlingService.displayPageLevelErrorMessage(error);
      }
    })
  }

  private resetAllFields() {
    this.clearDestinationCharity();
    this.clearOriginCharity();
    this.deactivateOriginCharityControl.setValue(true);
  }

  ngOnDestroy(): void {
    if (this.charityCategoriesSub) {
      this.charityCategoriesSub.unsubscribe();
    }
  }
}
